import { useEffect, useState } from "react";
import { api, money } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/Common";
import { toast } from "sonner";
import { Plus, BookOpen, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const blank = { name: "", description: "", category: "service", unit: "each", unit_price: 0, taxable: true };

export default function Catalog() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = async () => setItems((await api.get("/catalog")).data);
  useEffect(() => { load().catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (i) => { setEditing(i); setForm({ ...blank, ...i }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Item name is required");
    const payload = {
      name: form.name, description: form.description, category: form.category,
      unit: form.unit, unit_price: Number(form.unit_price) || 0, taxable: !!form.taxable,
    };
    try {
      if (editing) await api.put(`/catalog/${editing.id}`, payload);
      else await api.post("/catalog", payload);
      toast.success(editing ? "Item updated" : "Item added");
      setOpen(false);
      load();
    } catch {
      toast.error("Could not save item");
    }
  };

  const remove = async (i) => {
    if (!window.confirm(`Delete ${i.name}?`)) return;
    await api.delete(`/catalog/${i.id}`);
    toast.success("Item deleted");
    load();
  };

  return (
    <div data-testid="catalog-page">
      <PageHeader title="Price Catalog" subtitle="Reusable services and materials with default rates">
        <button data-testid="add-catalog-btn" onClick={openNew}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 text-sm font-medium transition-colors active:scale-95">
          <Plus size={16} /> Add Item
        </button>
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState icon={BookOpen} title="Catalog is empty"
          description="Save your common services and materials so quotes take seconds."
          action={<button onClick={openNew} data-testid="empty-add-catalog-btn"
            className="text-sm font-medium text-blue-600">Add your first item →</button>} />
      ) : (
        <div className="border border-slate-200 rounded-md bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="catalog-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left font-semibold px-4 py-2.5">Item</th>
                <th className="text-left font-semibold px-4 py-2.5">Category</th>
                <th className="text-left font-semibold px-4 py-2.5">Unit</th>
                <th className="text-right font-semibold px-4 py-2.5">Rate</th>
                <th className="text-center font-semibold px-4 py-2.5">GST</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} data-testid={`catalog-row-${i.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900">{i.name}</p>
                    {i.description && <p className="text-xs text-slate-500 mt-0.5">{i.description}</p>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 capitalize">{i.category}</td>
                  <td className="px-4 py-3 text-slate-600">{i.unit}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">{money(i.unit_price)}</td>
                  <td className="px-4 py-3 text-center text-xs text-slate-500">{i.taxable ? "Taxable" : "GST free"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button data-testid={`edit-catalog-${i.id}`} onClick={() => openEdit(i)} aria-label="Edit item"
                      className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors">
                      <Pencil size={15} />
                    </button>
                    <button data-testid={`delete-catalog-${i.id}`} onClick={() => remove(i)} aria-label="Delete item"
                      className="p-1.5 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors">
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="catalog-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>{editing ? "Edit item" : "Add catalog item"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <label className="block sm:col-span-2">
              <span className="text-xs font-medium text-slate-600">Name *</span>
              <input data-testid="catalog-name-input" value={form.name} onChange={(e) => set("name", e.target.value)}
                placeholder="Install 15mm copper pipe"
                className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-xs font-medium text-slate-600">Description</span>
              <input data-testid="catalog-description-input" value={form.description} onChange={(e) => set("description", e.target.value)}
                className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Category</span>
              <select data-testid="catalog-category-select" value={form.category} onChange={(e) => set("category", e.target.value)}
                className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="service">Service</option>
                <option value="material">Material</option>
                <option value="labour">Labour</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Unit</span>
              <input data-testid="catalog-unit-input" value={form.unit} onChange={(e) => set("unit", e.target.value)}
                className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Rate</span>
              <input data-testid="catalog-price-input" type="number" step="0.01" value={form.unit_price}
                onChange={(e) => set("unit_price", e.target.value)}
                className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </label>
            <label className="flex items-center gap-2 mt-6">
              <input data-testid="catalog-taxable-checkbox" type="checkbox" checked={!!form.taxable}
                onChange={(e) => set("taxable", e.target.checked)} className="h-4 w-4 accent-blue-600" />
              <span className="text-sm text-slate-700">GST applies</span>
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)}
              className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-md px-4 py-2 text-sm font-medium">Cancel</button>
            <button data-testid="save-catalog-btn" onClick={save}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 text-sm font-medium">Save item</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
